// True-vector PDF: walks the rendered DOM and re-draws boxes, borders, images and text with
// jsPDF using the browser's own layout positions, so output is crisp at any zoom and the text
// stays selectable. US kit pages embed Archivo; AU pages use the PDF-native Helvetica (metric
// twin of Arial, which the AU template renders in). SVG images are drawn as real vectors.
import jsPDF from 'jspdf';
import 'svg2pdf.js';

const PX = 0.75; // 96dpi px -> pt
const WEIGHTS = [400, 500, 700, 800, 900];
const fontCache = {};

async function loadArchivo(pdf) {
  for (const w of WEIGHTS) {
    if (!fontCache[w]) {
      const buf = await fetch(`${process.env.PUBLIC_URL || ''}/fonts/Archivo-${w}.ttf`).then(r => r.arrayBuffer());
      let bin = ''; const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
      fontCache[w] = btoa(bin);
    }
    pdf.addFileToVFS(`Archivo-${w}.ttf`, fontCache[w]);
    pdf.addFont(`Archivo-${w}.ttf`, 'Archivo', `w${w}`);
  }
}

const weightStyle = (fw) => { const n = parseInt(fw, 10) || (fw === 'bold' ? 700 : 400); return 'w' + WEIGHTS.reduce((a, b) => Math.abs(b - n) < Math.abs(a - n) ? b : a); };
const isBold = (fw) => (parseInt(fw, 10) || (fw === 'bold' ? 700 : 400)) >= 600;

function rgba(str) {
  const m = /rgba?\(([^)]+)\)/.exec(str || '');
  if (!m) return null;
  const p = m[1].split(/[\s,/]+/).map(s => parseFloat(s));
  if (p.length > 3 && p[3] === 0) return null;
  return p.slice(0, 3);
}

// Which font stack a page renders in. usds-kit pages are Archivo; everything else Arial-ish.
export const pageMode = (page) => page.classList.contains('usds-kit') ? 'archivo'
  : (page.classList.contains('ds-page') && !page.classList.contains('ds-cover2') && !page.classList.contains('usds')) ? 'helvetica'
    : null;

function setFont(pdf, mode, cs) {
  if (mode === 'archivo') { pdf.setFont('Archivo', weightStyle(cs.fontWeight)); return; }
  const b = isBold(cs.fontWeight), i = /italic|oblique/.test(cs.fontStyle);
  pdf.setFont('helvetica', b && i ? 'bolditalic' : b ? 'bold' : i ? 'italic' : 'normal');
}

// Ratio of (top of glyph box -> baseline) / glyph box height for the page font.
function measureBaseline(page) {
  const probe = document.createElement('div');
  probe.style.cssText = `position:absolute;left:0;top:0;font:100px ${getComputedStyle(page).fontFamily};line-height:normal;white-space:nowrap`;
  const t = document.createTextNode('Hg'); const mark = document.createElement('span');
  mark.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline';
  probe.appendChild(t); probe.appendChild(mark); page.appendChild(probe);
  const rg = document.createRange(); rg.selectNodeContents(t);
  const tr = rg.getBoundingClientRect(), mr = mark.getBoundingClientRect();
  page.removeChild(probe);
  return (mr.top - tr.top) / tr.height;
}

// Split a text node into rendered lines using per-character client rects.
function textLines(node, transform) {
  let text = node.data; if (transform === 'uppercase') text = text.toUpperCase();
  const range = document.createRange(); const lines = [];
  let cur = null;
  for (let i = 0; i < node.data.length; i++) {
    range.setStart(node, i); range.setEnd(node, i + 1);
    const rects = range.getClientRects(); if (!rects.length) continue;
    const r = rects[0]; const ch = text[i];
    if (r.width === 0) { if (/\s/.test(ch)) continue; }
    if (!cur || Math.abs(r.top - cur.top) > 1) { cur = { text: '', left: r.left, top: r.top, bottom: r.bottom, right: r.right }; lines.push(cur); }
    cur.text += ch; cur.bottom = Math.max(cur.bottom, r.bottom); cur.right = Math.max(cur.right, r.right);
  }
  return lines.map(l => ({ ...l, text: l.text.replace(/\s+$/, '') })).filter(l => l.text);
}

const imgCache = {};
const imageBytes = {};
function loadImage(src) {
  if (!imgCache[src]) imgCache[src] = new Promise((res) => { const im = new Image(); im.crossOrigin = 'anonymous'; im.onload = () => res(im); im.onerror = () => res(null); im.src = src; });
  return imgCache[src];
}

async function svgElementFrom(src) {
  let text;
  if (/^data:image\/svg\+xml/.test(src)) {
    const comma = src.indexOf(','); const meta = src.slice(0, comma), body = src.slice(comma + 1);
    text = /;base64/.test(meta) ? decodeURIComponent(escape(atob(body))) : decodeURIComponent(body);
  } else {
    const r = await fetch(src); if (!r.ok) throw new Error('svg fetch failed'); text = await r.text();
  }
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const el = doc.documentElement;
  if (!el || el.nodeName.toLowerCase() !== 'svg') throw new Error('not an svg');
  return el;
}

// Draw an SVG source as vectors into the given box (contain-fit already applied by caller).
async function drawSvg(pdf, src, x, y, w, h) {
  const el = await svgElementFrom(src);
  if (!el.getAttribute('viewBox')) {
    const vw = parseFloat(el.getAttribute('width')), vh = parseFloat(el.getAttribute('height'));
    if (vw > 0 && vh > 0) el.setAttribute('viewBox', `0 0 ${vw} ${vh}`);
  }
  el.setAttribute('width', String(w * PX)); el.setAttribute('height', String(h * PX));
  el.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  // svg2pdf needs computed styles, so the element must be attached while it renders.
  const holder = document.createElement('div');
  holder.style.cssText = 'position:absolute;left:-100000px;top:0;width:0;height:0;overflow:hidden';
  holder.appendChild(el); document.body.appendChild(holder);
  try { await pdf.svg(el, { x: x * PX, y: y * PX, width: w * PX, height: h * PX }); }
  finally { holder.remove(); }
}

// Embed a raster source at its native resolution (original bytes when we can get them).
async function drawRaster(pdf, src, im, x, y, w, h) {
  let data = src, fmt = /^data:image\/jpe?g/.test(src) ? 'JPEG' : 'PNG';
  if (!src.startsWith('data:')) {
    if (!imageBytes[src]) imageBytes[src] = fetch(src).then(async response => {
      if (!response.ok) throw new Error('Unable to load original artwork');
      const bytes = new Uint8Array(await response.arrayBuffer());
      return { bytes, format: bytes[0] === 0xff && bytes[1] === 0xd8 ? 'JPEG' : bytes[0] === 0x89 && bytes[1] === 0x50 ? 'PNG' : null };
    }).catch(() => ({ bytes: null, format: null }));
    const original = await imageBytes[src];
    if (original.format) { data = original.bytes; fmt = original.format; }
  }
  if (typeof data === 'string' && !/^data:image\/(png|jpe?g)/.test(data)) {
    const c = document.createElement('canvas');
    c.width = im.naturalWidth; c.height = im.naturalHeight;
    c.getContext('2d').drawImage(im, 0, 0, c.width, c.height); data = c.toDataURL('image/png'); fmt = 'PNG';
  }
  pdf.addImage(data, fmt, x * PX, y * PX, w * PX, h * PX, undefined, 'SLOW');
}

async function drawBgImage(pdf, el, cs, box) {
  const m = /url\("?([^")]+)"?\)/.exec(cs.backgroundImage); if (!m) return;
  const src = m[1];
  const im = await loadImage(src); if (!im || !im.naturalWidth) return;
  const s = Math.min(box.w / im.naturalWidth, box.h / im.naturalHeight);
  const w = im.naturalWidth * s, h = im.naturalHeight * s;
  const pos = cs.backgroundPosition || '';
  const x = box.x + (/right/.test(pos) ? box.w - w : /left/.test(pos) ? 0 : (box.w - w) / 2);
  const y = box.y + (box.h - h) / 2;
  if (/^data:image\/svg|\.svg(\?|$)/.test(src)) {
    try { await drawSvg(pdf, src, x, y, w, h); return; } catch (e) { console.warn('SVG background fell back to raster', e); }
  }
  await drawRaster(pdf, src, im, x, y, w, h);
}

// <img>: honour object-fit (contain by default in the datasheet CSS) inside the element box.
async function drawImgElement(pdf, el, cs, box) {
  const src = el.currentSrc || el.src; if (!src) return;
  const im = await loadImage(src); if (!im || !im.naturalWidth) return;
  let x = box.x, y = box.y, w = box.w, h = box.h;
  if (cs.objectFit === 'contain' || cs.objectFit === 'scale-down') {
    const s = Math.min(box.w / im.naturalWidth, box.h / im.naturalHeight);
    w = im.naturalWidth * s; h = im.naturalHeight * s;
    x = box.x + (box.w - w) / 2; y = box.y + (box.h - h) / 2;
  }
  if (/^data:image\/svg|\.svg(\?|$)/.test(src)) {
    try { await drawSvg(pdf, src, x, y, w, h); return; } catch (e) { console.warn('SVG image fell back to raster', e); }
  }
  await drawRaster(pdf, src, im, x, y, w, h);
}

function drawBorders(pdf, cs, box) {
  const widths = ['Top', 'Right', 'Bottom', 'Left'].map(s => parseFloat(cs[`border${s}Width`]) || 0);
  if (!widths.some(w => w > 0)) return;
  const styles = ['Top', 'Right', 'Bottom', 'Left'].map(s => cs[`border${s}Style`]);
  const dashed = styles.some(s => s === 'dashed' || s === 'dotted');
  if (dashed && widths.every(w => w === widths[0]) && styles.every(s => s === styles[0])) {
    const c = rgba(cs.borderTopColor); if (!c) return;
    const w = widths[0];
    pdf.setDrawColor(...c); pdf.setLineWidth(w * PX); pdf.setLineDashPattern([3 * PX, 3 * PX], 0);
    const r = parseFloat(cs.borderTopLeftRadius) || 0;
    if (r > 0) pdf.roundedRect((box.x + w / 2) * PX, (box.y + w / 2) * PX, (box.w - w) * PX, (box.h - w) * PX, r * PX, r * PX, 'S');
    else pdf.rect((box.x + w / 2) * PX, (box.y + w / 2) * PX, (box.w - w) * PX, (box.h - w) * PX, 'S');
    pdf.setLineDashPattern([], 0);
    return;
  }
  const sides = [['Top', 0, 0, box.w, widths[0]], ['Bottom', 0, box.h - widths[2], box.w, widths[2]],
    ['Left', 0, 0, widths[3], box.h], ['Right', box.w - widths[1], 0, widths[1], box.h]];
  for (const [side, dx, dy, w, h] of sides) {
    if (!(w > 0 && h > 0) || cs[`border${side}Style`] === 'none' || cs[`border${side}Style`] === 'hidden') continue;
    const c = rgba(cs[`border${side}Color`]); if (!c) continue;
    pdf.setFillColor(...c); pdf.rect((box.x + dx) * PX, (box.y + dy) * PX, w * PX, h * PX, 'F');
  }
}

// Absolutely-positioned ::before/::after boxes with a solid background (e.g. the AU header rule).
function drawPseudo(pdf, el, box, which) {
  const ps = getComputedStyle(el, which);
  if (!ps || ps.content === 'none' || ps.content === '' || ps.position !== 'absolute') return;
  const bg = rgba(ps.backgroundColor); if (!bg) return;
  const n = (v) => (v === 'auto' ? null : parseFloat(v));
  const top = n(ps.top), left = n(ps.left), right = n(ps.right), bottom = n(ps.bottom);
  let w = n(ps.width), h = n(ps.height);
  if (w == null && left != null && right != null) w = box.w - left - right;
  if (h == null && top != null && bottom != null) h = box.h - top - bottom;
  if (!(w > 0 && h > 0)) return;
  const x = box.x + (left != null ? left : right != null ? box.w - right - w : 0);
  const y = box.y + (top != null ? top : bottom != null ? box.h - bottom - h : 0);
  pdf.setFillColor(...bg); pdf.rect(x * PX, y * PX, w * PX, h * PX, 'F');
}

async function drawElement(pdf, el, origin) {
  const cs = getComputedStyle(el); const r = el.getBoundingClientRect();
  const box = { x: r.left - origin.x, y: r.top - origin.y, w: r.width, h: r.height };
  if (box.w <= 0 || box.h <= 0) return;
  const bg = rgba(cs.backgroundColor);
  const radius = parseFloat(cs.borderTopLeftRadius) || 0;
  if (bg) {
    pdf.setFillColor(...bg);
    if (radius >= Math.min(box.w, box.h) / 2 - 0.5) pdf.circle((box.x + box.w / 2) * PX, (box.y + box.h / 2) * PX, (Math.min(box.w, box.h) / 2) * PX, 'F');
    else if (radius > 0) pdf.roundedRect(box.x * PX, box.y * PX, box.w * PX, box.h * PX, radius * PX, radius * PX, 'F');
    else pdf.rect(box.x * PX, box.y * PX, box.w * PX, box.h * PX, 'F');
  }
  drawBorders(pdf, cs, box);
  drawPseudo(pdf, el, box, '::before'); drawPseudo(pdf, el, box, '::after');
  if (cs.backgroundImage && cs.backgroundImage !== 'none') await drawBgImage(pdf, el, cs, box);
  if (el.tagName === 'IMG') await drawImgElement(pdf, el, cs, box);
  else if (el.tagName === 'svg') {
    const clone = el.cloneNode(true);
    const holder = document.createElement('div');
    holder.style.cssText = 'position:absolute;left:-100000px;top:0;width:0;height:0;overflow:hidden';
    holder.appendChild(clone); document.body.appendChild(holder);
    try { await pdf.svg(clone, { x: box.x * PX, y: box.y * PX, width: box.w * PX, height: box.h * PX }); } finally { holder.remove(); }
  }
  // Keep anchors clickable in the exported PDF (footer website / email).
  if (el.tagName === 'A' && el.getAttribute('href')) {
    pdf.link(box.x * PX, box.y * PX, box.w * PX, box.h * PX, { url: el.href });
  }
}

function drawText(pdf, node, origin, baseRatio, mode) {
  const el = node.parentElement; const cs = getComputedStyle(el);
  const color = rgba(cs.color); if (!color) return; // color: transparent
  const size = parseFloat(cs.fontSize); const ls = parseFloat(cs.letterSpacing) || 0;
  setFont(pdf, mode, cs); pdf.setFontSize(size * PX); pdf.setTextColor(...color); pdf.setCharSpace(ls * PX);
  const underline = /underline/.test(cs.textDecorationLine || cs.textDecoration || '');
  for (const l of textLines(node, cs.textTransform)) {
    const baseline = l.top + (l.bottom - l.top) * baseRatio;
    pdf.text(l.text, (l.left - origin.x) * PX, (baseline - origin.y) * PX, { baseline: 'alphabetic' });
    if (underline) {
      pdf.setDrawColor(...color); pdf.setLineWidth(Math.max(0.5, size * 0.06) * PX);
      const uy = (baseline - origin.y + size * 0.1) * PX;
      pdf.line((l.left - origin.x) * PX, uy, (l.right - origin.x) * PX, uy);
    }
  }
  pdf.setCharSpace(0);
}

function drawMarker(pdf, li, origin) {
  const cs = getComputedStyle(li); if (cs.listStyleType === 'none' || cs.display !== 'list-item') return;
  const tn = document.createTreeWalker(li, NodeFilter.SHOW_TEXT).nextNode(); if (!tn) return;
  const rg = document.createRange(); rg.setStart(tn, 0); rg.setEnd(tn, Math.min(1, tn.data.length));
  const r = rg.getClientRects()[0]; if (!r) return;
  const lr = li.getBoundingClientRect(); const size = parseFloat(cs.fontSize);
  pdf.setFillColor(...(rgba(cs.color) || [0, 0, 0]));
  pdf.circle((lr.left - size * 0.75 - origin.x) * PX, ((r.top + r.bottom) / 2 - origin.y) * PX, size * 0.14 * PX, 'F');
}

const hidden = (el) => { const cs = getComputedStyle(el); return cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0; };

async function drawPage(pdf, page, baseRatio, mode) {
  const pr = page.getBoundingClientRect(); const origin = { x: pr.left, y: pr.top };
  const walker = document.createTreeWalker(page, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      if (n.parentNode && n.parentNode.namespaceURI === 'http://www.w3.org/2000/svg') return NodeFilter.FILTER_REJECT; // drawn by svg2pdf
      if (n.nodeType === Node.ELEMENT_NODE && n !== page && hidden(n)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n = walker.nextNode();
  while (n) {
    if (n.nodeType === Node.ELEMENT_NODE) {
      await drawElement(pdf, n, origin);
      if (n.tagName === 'LI') drawMarker(pdf, n, origin);
    } else if (n.data.trim()) drawText(pdf, n, origin, baseRatio, mode);
    n = walker.nextNode();
  }
}

// pages: DOM nodes. rasterize(page) -> { dataUrl } is used for pages we cannot vectorise
// (proposal covers); vector pages that throw fall back to it too, so an export never fails.
export async function buildVectorPdf(pages, meta = {}, rasterize = null) {
  const size = (p) => [p.offsetWidth * PX, p.offsetHeight * PX];
  const [w0, h0] = size(pages[0]);
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: [w0, h0], compress: true });
  if (meta.title) pdf.setProperties({ title: meta.title, subject: meta.subject || '' });
  if (pages.some(p => pageMode(p) === 'archivo')) await loadArchivo(pdf);
  const baseRatios = new Map();
  const vectorised = [];
  for (let i = 0; i < pages.length; i++) {
    const page = pages[i]; const [w, h] = size(page);
    if (i > 0) pdf.addPage([w, h], 'portrait');
    const mode = pageMode(page);
    let ok = false;
    if (mode) {
      const family = getComputedStyle(page).fontFamily;
      if (!baseRatios.has(family)) baseRatios.set(family, measureBaseline(page));
      try { await drawPage(pdf, page, baseRatios.get(family), mode); ok = true; }
      catch (e) { console.warn('Vector page failed; rasterising this page', e); }
    }
    if (!ok) {
      if (!rasterize) throw new Error('Page cannot be vectorised and no rasteriser was supplied');
      const { dataUrl } = await rasterize(page);
      pdf.addImage(dataUrl, 'PNG', 0, 0, w, h, undefined, 'FAST');
    }
    vectorised.push(ok);
  }
  pdf.__vectorised = vectorised;
  return pdf;
}
